import React, { useCallback, useEffect, useState } from 'react';
import {
  crmService,
  Client,
  ClientNote,
  CrmActivityItem,
  TenantUserOption,
  datetimeLocalToIso,
  isoToDatetimeLocalValue,
} from '../../services/crmService';
import { formatPhoneDisplay } from '../../utils/phoneFormat';
import { displayEmail } from '../../utils/placeholderEmail';
import { leadSourceBadgeClass } from '../../utils/statusStyles';
import {
  CONVERSION_ACTIONS,
  ConversionAction,
  LOST_REASONS,
  STAGE_META,
} from './stageConfig';

interface ContactDetailPanelProps {
  contactId: string;
  onClose: () => void;
  /** Called after any mutation so parent lists/dashboards can refresh. */
  onChanged?: (client: Client) => void;
  users?: TenantUserOption[];
}

function relativeTime(iso?: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso).getTime();
  const diff = Date.now() - d;
  const min = Math.round(diff / 60000);
  if (Math.abs(min) < 60) return min <= 0 ? 'just now' : `${min}m ago`;
  const hr = Math.round(min / 60);
  if (Math.abs(hr) < 24) return `${hr}h ago`;
  const day = Math.round(hr / 24);
  if (Math.abs(day) < 30) return `${day}d ago`;
  return new Date(iso).toLocaleDateString();
}

const conversionButtonClass = (intent: ConversionAction['intent']) => {
  if (intent === 'primary') return 'btn-primary';
  if (intent === 'danger') return 'bg-danger-fg text-white hover:opacity-90';
  return 'btn-secondary';
};

/** Inline-editable text field. */
const EditableField: React.FC<{
  label: string;
  value: string;
  onSave: (v: string) => void;
  type?: string;
  placeholder?: string;
}> = ({ label, value, onSave, type = 'text', placeholder }) => {
  const [draft, setDraft] = useState(value ?? '');
  useEffect(() => setDraft(value ?? ''), [value]);
  return (
    <label className="block">
      <span className="mb-1 block text-xs font-medium text-fg-muted">{label}</span>
      <input
        type={type}
        value={draft}
        placeholder={placeholder}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={() => draft !== (value ?? '') && onSave(draft)}
        className="input-field w-full rounded-xl border border-border bg-input px-3 py-3 text-base text-fg"
      />
    </label>
  );
};

const ContactDetailPanel: React.FC<ContactDetailPanelProps> = ({ contactId, onClose, onChanged, users }) => {
  const [client, setClient] = useState<Client | null>(null);
  const [notes, setNotes] = useState<ClientNote[]>([]);
  const [activity, setActivity] = useState<CrmActivityItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [noteDraft, setNoteDraft] = useState('');
  const [lostFor, setLostFor] = useState<ConversionAction | null>(null);
  const [lostReason, setLostReason] = useState('price');
  const [followUpLocal, setFollowUpLocal] = useState('');

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const [c, n, a] = await Promise.all([
        crmService.getClient(contactId),
        crmService.getClientNotes(contactId).catch(() => []),
        crmService.getClientActivity(contactId).catch(() => []),
      ]);
      setClient(c);
      setNotes(n);
      setActivity(a);
      setFollowUpLocal(isoToDatetimeLocalValue(c.next_follow_up_at));
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load contact');
    } finally {
      setLoading(false);
    }
  }, [contactId]);

  useEffect(() => {
    load();
  }, [load]);

  const patch = useCallback(
    async (data: Partial<Client>) => {
      try {
        const updated = await crmService.updateClient(contactId, data);
        setClient(updated);
        onChanged?.(updated);
        // refresh timeline (stage changes/notes create activity)
        crmService.getClientActivity(contactId).then(setActivity).catch(() => undefined);
        return updated;
      } catch (e) {
        setError(e instanceof Error ? e.message : 'Update failed');
      }
    },
    [contactId, onChanged]
  );

  const runConversion = (action: ConversionAction) => {
    if (action.requiresLostReason) {
      setLostFor(action);
      return;
    }
    patch({ stage: action.toStage });
  };

  const confirmLost = () => {
    if (!lostFor) return;
    patch({ stage: lostFor.toStage, lost_reason: lostReason });
    setLostFor(null);
  };

  const saveNote = async () => {
    const content = noteDraft.trim();
    if (!content) return;
    await crmService.createClientNote(contactId, { title: '', content });
    setNoteDraft('');
    const [n, a] = await Promise.all([
      crmService.getClientNotes(contactId).catch(() => notes),
      crmService.getClientActivity(contactId).catch(() => activity),
    ]);
    setNotes(n);
    setActivity(a);
  };

  const stageMeta = client ? STAGE_META[client.stage] : null;
  const actions = client ? CONVERSION_ACTIONS[client.stage] : [];

  return (
    <div className="fixed inset-0 z-50 flex justify-end">
      <div className="absolute inset-0 bg-overlay/60" onClick={onClose} aria-hidden />
      <aside
        role="dialog"
        aria-label="Contact detail"
        className="relative flex h-full w-full flex-col overflow-y-auto bg-surface shadow-xl sm:max-w-md"
      >
        {loading && !client ? (
          <div className="p-8 text-center text-fg-muted">Loading…</div>
        ) : !client ? (
          <div className="p-8 text-center text-danger-fg">{error || 'Not found.'}</div>
        ) : (
          <>
            {/* Header */}
            <div className="sticky top-0 z-10 border-b border-border bg-surface px-5 py-4">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <h2 className="truncate text-xl font-bold text-fg">{client.full_name}</h2>
                    {stageMeta ? (
                      <span className={`rounded-full border px-2 py-0.5 text-xs font-semibold ${stageMeta.badgeClass}`}>
                        {stageMeta.singularLabel}
                      </span>
                    ) : null}
                  </div>
                  {client.company_name ? (
                    <p className="truncate text-sm text-fg-muted">{client.company_name}</p>
                  ) : null}
                </div>
                <button type="button" onClick={onClose} aria-label="Close" className="text-fg-muted hover:text-fg">
                  <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              {actions.length > 0 ? (
                <div className="mt-3 flex flex-wrap gap-2">
                  {actions.map((a) => (
                    <button
                      key={a.id}
                      type="button"
                      onClick={() => runConversion(a)}
                      aria-label={`${a.label} — ${client.full_name}`}
                      className={`rounded-xl px-4 py-2 text-sm font-semibold ${conversionButtonClass(a.intent)}`}
                    >
                      {a.label}
                    </button>
                  ))}
                </div>
              ) : null}

              {lostFor ? (
                <div className="mt-3 rounded-xl border border-danger-fg/30 bg-danger-fg/5 p-3">
                  <label className="block text-xs font-medium text-fg-muted">Reason for {lostFor.label.toLowerCase()}</label>
                  <div className="mt-2 flex gap-2">
                    <select
                      value={lostReason}
                      onChange={(e) => setLostReason(e.target.value)}
                      className="input-field flex-1 rounded-xl border border-border bg-input px-3 py-2 text-sm text-fg"
                    >
                      {LOST_REASONS.map((r) => (
                        <option key={r.value} value={r.value}>{r.label}</option>
                      ))}
                    </select>
                    <button type="button" onClick={confirmLost} className="rounded-xl bg-danger-fg px-4 py-2 text-sm font-semibold text-white">
                      Confirm
                    </button>
                    <button type="button" onClick={() => setLostFor(null)} className="btn-secondary rounded-xl px-3 py-2 text-sm">
                      Cancel
                    </button>
                  </div>
                </div>
              ) : null}
            </div>

            <div className="space-y-6 px-5 py-5">
              {error ? <div className="alert-danger rounded-xl px-3 py-2 text-sm">{error}</div> : null}

              {/* Tap-to-call / tap-to-email — highest-value mobile interaction */}
              <div className="grid grid-cols-2 gap-3">
                <a
                  href={client.phone ? `tel:${client.phone}` : undefined}
                  aria-disabled={!client.phone}
                  aria-label={client.phone ? `Call ${client.full_name} at ${client.phone}` : 'No phone number'}
                  className={`flex items-center justify-center gap-2 rounded-2xl px-4 py-4 text-base font-semibold ${
                    client.phone ? 'btn-primary' : 'cursor-not-allowed bg-surface-muted text-fg-muted'
                  }`}
                >
                  <span aria-hidden>📞</span> Call
                </a>
                <a
                  href={displayEmail(client.email) ? `mailto:${displayEmail(client.email)}` : undefined}
                  aria-disabled={!displayEmail(client.email)}
                  aria-label={
                    displayEmail(client.email)
                      ? `Email ${client.full_name} at ${displayEmail(client.email)}`
                      : 'No email address'
                  }
                  className={`flex items-center justify-center gap-2 rounded-2xl px-4 py-4 text-base font-semibold ${
                    displayEmail(client.email) ? 'btn-secondary' : 'cursor-not-allowed bg-surface-muted text-fg-muted'
                  }`}
                >
                  <span aria-hidden>✉️</span> Email
                </a>
              </div>
              <p className="-mt-4 text-center text-xs text-fg-muted">
                {formatPhoneDisplay(client.phone) || 'No phone'} · {displayEmail(client.email) || 'No email'}
              </p>

              {/* Contact + qualification fields (inline editable) */}
              <div className="grid grid-cols-1 gap-4">
                <EditableField label="Name" value={client.full_name} onSave={(v) => patch({ full_name: v })} />
                <EditableField label="Company" value={client.company_name} onSave={(v) => patch({ company_name: v })} />
                <EditableField label="Phone" type="tel" value={client.phone} onSave={(v) => patch({ phone: v })} />
                <EditableField label="Email" type="email" value={displayEmail(client.email)} onSave={(v) => patch({ email: v })} />
                <EditableField label="Service requested" value={client.service_requested || ''} onSave={(v) => patch({ service_requested: v })} />
                <EditableField label="Service location" value={client.service_location || ''} onSave={(v) => patch({ service_location: v })} />
                <EditableField label="Property type" value={client.property_type || ''} onSave={(v) => patch({ property_type: v })} />

                <div className="flex items-center gap-2">
                  {client.source ? <span className={leadSourceBadgeClass(client.source)}>{client.source}</span> : null}
                </div>

                {/* Assigned user */}
                <label className="block">
                  <span className="mb-1 block text-xs font-medium text-fg-muted">Assigned to</span>
                  <select
                    value={client.assigned_to ?? ''}
                    onChange={(e) => patch({ assigned_to: e.target.value || null })}
                    className="input-field w-full rounded-xl border border-border bg-input px-3 py-3 text-base text-fg"
                  >
                    <option value="">Unassigned</option>
                    {(users ?? []).map((u) => (
                      <option key={u.id} value={u.id}>
                        {`${u.first_name || ''} ${u.last_name || ''}`.trim() || u.email}
                      </option>
                    ))}
                  </select>
                </label>

                {/* Next follow-up */}
                <label className="block">
                  <span className="mb-1 block text-xs font-medium text-fg-muted">Next follow-up</span>
                  <input
                    type="datetime-local"
                    value={followUpLocal}
                    onChange={(e) => setFollowUpLocal(e.target.value)}
                    onBlur={() => patch({ next_follow_up_at: datetimeLocalToIso(followUpLocal) })}
                    className="input-field w-full rounded-xl border border-border bg-input px-3 py-3 text-base text-fg"
                  />
                </label>
              </div>

              {/* Note composer */}
              <div>
                <h3 className="mb-2 text-sm font-semibold text-fg">Add a note</h3>
                <textarea
                  value={noteDraft}
                  onChange={(e) => setNoteDraft(e.target.value)}
                  rows={3}
                  placeholder="Log a call, next step, or detail…"
                  className="input-field w-full rounded-xl border border-border bg-input px-3 py-3 text-base text-fg"
                />
                <button type="button" onClick={saveNote} disabled={!noteDraft.trim()} className="btn-primary mt-2 rounded-xl px-4 py-2 text-sm disabled:opacity-50">
                  Save note
                </button>
              </div>

              {/* Activity timeline */}
              <div>
                <h3 className="mb-2 text-sm font-semibold text-fg">Activity</h3>
                {activity.length === 0 ? (
                  <p className="text-sm text-fg-muted">No activity yet.</p>
                ) : (
                  <ul className="space-y-3">
                    {activity.map((item) => (
                      <li key={item.id} className="rounded-xl border border-border bg-surface-muted/30 p-3">
                        <div className="flex items-center justify-between gap-2">
                          <span className="text-sm font-medium text-fg">{item.description}</span>
                          <span className="shrink-0 text-xs text-fg-muted">{relativeTime(item.created_at)}</span>
                        </div>
                        {item.metadata?.from_stage && item.metadata?.to_stage ? (
                          <p className="mt-1 text-xs text-fg-muted">
                            {item.metadata.from_stage} → {item.metadata.to_stage}
                            {item.metadata.lost_reason ? ` (${item.metadata.lost_reason})` : ''}
                          </p>
                        ) : null}
                        {item.metadata?.capture_type === 'chatbot' ? (
                          <p className="mt-1 text-xs text-primary">Chatbot conversation</p>
                        ) : null}
                        {item.user_name ? <p className="mt-1 text-xs text-fg-muted">by {item.user_name}</p> : null}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
          </>
        )}
      </aside>
    </div>
  );
};

export default ContactDetailPanel;
